// src/pages/bloomterminal/ManageMembership.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, CheckCircle2 } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { getCommodities } from '../utils/api';

const ManageMembership = () => {
  const { email, userDetails } = useUser();
  const [commodities, setCommodities] = useState([]);
  const [loadingCommodities, setLoadingCommodities] = useState(true);
  
  // Plan states
  const [selectedPlan, setSelectedPlan] = useState(null); // Track user's selection
  const [advancedCommodities, setAdvancedCommodities] = useState([]);
  const [advancedDropdownOpen, setAdvancedDropdownOpen] = useState(false);
  const advancedDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (advancedDropdownRef.current && !advancedDropdownRef.current.contains(event.target)) {
        setAdvancedDropdownOpen(false);
      }
    };

    if (advancedDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [advancedDropdownOpen]);

  useEffect(() => {
    const fetchCommodities = async () => {
      if (!email) return;
      
      try {
        setLoadingCommodities(true);
        const response = await getCommodities(email);
        if (response?.data) {
          setCommodities(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch commodities:', error);
      } finally {
        setLoadingCommodities(false);
      }
    };

    fetchCommodities();
  }, [email]);

  const handleAdvancedCommodityToggle = (commodity) => {
    if (advancedCommodities.includes(commodity)) {
      setAdvancedCommodities(advancedCommodities.filter(c => c !== commodity));
    } else if (advancedCommodities.length < 10) {
      setAdvancedCommodities([...advancedCommodities, commodity]);
    }
  };

  const handleChoosePlan = async (planKey) => {
    // Validate commodity selection based on plan
    if (planKey === 'advanced' && advancedCommodities.length < 1) {
      return; // Button should be disabled anyway
    }
    // Elite plan always has all commodities included, no validation needed

    // TODO: Implement subscription API call
    console.log(`Choosing ${planKey} plan`, {
      plan: planKey,
      advancedCommodities,
      eliteAllCommodities: planKey === 'elite' ? 'all' : null
    });
    
    // Here you would make the API call to update the subscription
    // await updateSubscription(email, planKey, { 
    //   advancedCommodities, 
    //   eliteAllCommodities: planKey === 'elite' ? 'all' : null 
    // });
    
    alert(`Subscription functionality will be implemented here for ${planKey} plan`);
  };

  const canChooseAdvanced = advancedCommodities.length >= 1;
  const canChooseElite = true; // Elite always has all commodities included

  // Initialize selected plan based on user's current tier
  useEffect(() => {
    if (userDetails?.tier && !selectedPlan) {
      const currentTier = userDetails.tier.toLowerCase();
      setSelectedPlan(currentTier);
    }
  }, [userDetails?.tier, selectedPlan]);

  // Determine selected plan - use user selection if available, otherwise use current tier
  const currentTier = userDetails?.tier?.toLowerCase() || '';
  const activePlan = selectedPlan || currentTier;
  const isAdvancedSelected = activePlan === 'advanced';
  const isEliteSelected = activePlan === 'elite';

  const handlePlanSelect = (planKey) => {
    setSelectedPlan(planKey);
    // Close dropdown if open
    setAdvancedDropdownOpen(false);
  };

  const plans = [
    {
      name: 'Advanced',
      tierKey: 'advanced',
      price: '$149',
      isSelected: isAdvancedSelected,
      commoditySelection: (
        <div className="flex flex-col gap-[8px] items-start w-full">
          <label className="font-['Inter'] font-normal text-[12px] leading-[14.4px] text-[#f0f5f2]">
            Select up to 10 Commodities
          </label>
          <div className="flex items-center gap-[8px] w-full">
            <span className="font-['Inter'] font-medium text-[14px] leading-[16.8px] text-bloom-green">
              {advancedCommodities.length}/10 selected
            </span>
          </div>
          <div className="relative w-full" ref={advancedDropdownRef}>
            <button
              onClick={() => setAdvancedDropdownOpen(!advancedDropdownOpen)}
              className="bg-[#282a29] border border-bloom-green rounded-[8px] h-[32px] px-[12px] py-[8px] w-full flex items-center justify-between font-['Inter'] font-medium text-[12px] leading-[14.4px] text-[#f0f5f2]"
            >
              <span>Commodity</span>
              <ChevronDown 
                size={16} 
                className={`text-[#f0f5f2] transition-transform ${advancedDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {advancedDropdownOpen && (
              <div className="absolute z-10 mt-[4px] bg-[#282a29] border border-bloom-green rounded-[8px] w-full max-h-[200px] overflow-y-auto">
                {commodities.map((commodity) => (
                  <button
                    key={commodity}
                    onClick={() => handleAdvancedCommodityToggle(commodity)}
                    className={`w-full px-[12px] py-[8px] text-left flex items-center gap-[8px] hover:bg-[#1d1d1d] ${
                      advancedCommodities.includes(commodity) ? 'bg-[#1d1d1d]' : ''
                    }`}
                  >
                    <div className={`w-[16px] h-[16px] border border-bloom-green rounded-[4px] flex items-center justify-center ${
                      advancedCommodities.includes(commodity) ? 'bg-bloom-green' : ''
                    }`}>
                      {advancedCommodities.includes(commodity) && (
                        <Check size={12} className="text-[#f0f5f2]" />
                      )}
                    </div>
                    <span className="font-['Inter'] font-medium text-[12px] leading-[14.4px] text-[#f0f5f2]">
                      {commodity.charAt(0).toUpperCase() + commodity.slice(1)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="font-['Inter'] font-normal italic text-[12px] leading-[14.4px] text-[#828382]">
            You can change your selection once every 30 days
          </p>
        </div>
      ),
      features: [
        'Up to 10 commodities of your choice',
        '21 basic reports per week',
        '100 configurable alerts',
        '1000 BiQ queries per week',
        'All analytics features',
        { text: 'Desktop web app access', highlight: true },
      ],
      canChoose: canChooseAdvanced,
      errorMessage: 'Please select at least 1 commodity',
    },
    {
      name: 'Elite',
      tierKey: 'elite',
      price: '$1000',
      isSelected: isEliteSelected,
      commoditySelection: (
        <div className="flex gap-[8px] items-center">
          <CheckCircle2 size={24} className="text-bloom-green flex-shrink-0" />
          <span className="font-['Inter'] font-medium text-[14px] leading-[21px] text-bloom-green">
            All commodities included
          </span>
        </div>
      ),
      features: [
        'All available commodities',
        'Unlimited basic reports',
        'Unlimited configurable alerts',
        'Unlimited BiQ queries per week',
        'All analytics features',
        { text: 'Desktop web app access', highlight: true },
        { text: 'Retail program planning module', highlight: true },
        { text: 'Personal account manager', highlight: true },
        { text: 'Custom functionality', highlight: true },
      ],
      canChoose: canChooseElite,
      errorMessage: 'Please select at least 1 commodity',
    },
  ];

  return (
    <div className="w-full h-full overflow-x-hidden">
      {/* Main container */}
      <div className="w-full max-w-[1200px] mx-auto py-[48px] px-[16px] md:px-[24px]">
        <div className="flex flex-col gap-[64px] items-center w-full">
          {/* Title */}
          <div className="flex flex-col gap-[16px] items-center w-full">
            <h1 className="font-['Inter'] font-medium text-[24px] leading-[36px] text-[#f0f5f2] text-center">
              Manage your subscription
            </h1>
          </div>

          {/* Plan Cards */}
          {/* Mobile: cards full width up to 540px, centered */}
          {/* Tablet: container max-width 540px and centered */}
          {/* Desktop: full width with 2 cards side by side */}
          <div className="flex flex-col md:flex-col lg:flex-row gap-[24px] w-full md:max-w-[540px] lg:max-w-none mx-auto lg:mx-0 items-center md:items-center lg:items-start">
            {plans.map((plan) => (
              <div
                key={plan.name}
                onClick={() => handlePlanSelect(plan.tierKey)}
                className={`w-full max-w-[540px] md:max-w-full lg:max-w-none lg:flex-1 border-2 rounded-[8px] p-[24px] md:p-[32px] flex flex-col gap-[20px] min-h-[745px] cursor-pointer transition-all ${
                  plan.isSelected 
                    ? 'border-bloom-green bg-[#282a29]' 
                    : 'border-[#5d625f] bg-transparent hover:border-bloom-green/50'
                }`}
              >
                {/* Plan Header */}
                <div className="flex flex-col gap-[20px] items-center">
                  <h2 className="font-['Inter'] font-medium text-[24px] leading-[36px] text-white text-center">
                    {plan.name}
                  </h2>
                  <p className="font-['Inter'] font-medium text-[20px] leading-[30px] text-white text-center">
                    {plan.price} / month
                  </p>
                  <div className="h-[1px] w-full bg-[#5d625f]"></div>
                </div>

                {/* Commodity Selection */}
                <div 
                  className="flex flex-col gap-[20px]"
                  onClick={(e) => e.stopPropagation()} // Prevent card selection when clicking on commodity inputs
                >
                  {plan.commoditySelection}
                </div>

                {/* Features */}
                <div className="flex-1 border-t border-[#5d625f] pt-[12px] flex flex-col gap-[16px]">
                  <p className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#f0f5f2]">
                    What's included
                  </p>
                  <div className="flex flex-col gap-[8px]">
                    {plan.features.map((feature, index) => {
                      const featureText = typeof feature === 'object' ? feature.text : feature;
                      const isHighlighted = typeof feature === 'object' ? feature.highlight : false;
                      
                      return (
                        <div key={index} className="flex gap-[8px] items-center">
                          <Check size={24} className="text-bloom-green flex-shrink-0" />
                          <span
                            className={`font-['Inter'] font-normal text-[14px] leading-[21px] ${
                              isHighlighted
                                ? 'text-[#fef574] font-semibold'
                                : 'text-[#f0f5f2]'
                            }`}
                          >
                            {featureText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Button */}
                <div 
                  className="flex flex-col gap-[20px]"
                  onClick={(e) => e.stopPropagation()} // Prevent card selection when clicking button
                >
                  <button
                    onClick={() => handleChoosePlan(plan.tierKey)}
                    disabled={!plan.canChoose}
                    className={`w-full bg-bloom-green hover:bg-bloom-green/90 disabled:bg-bloom-green/50 disabled:cursor-not-allowed transition-colors px-[20px] py-[12px] rounded-[8px] font-['Helvetica_Neue'] font-medium text-[16px] leading-[24px] text-[#f0f5f2]`}
                  >
                    Choose {plan.name}
                  </button>
                  <div className="flex flex-col gap-[8px] items-center text-center">
                    {!plan.canChoose && (
                      <p className="font-['Inter'] font-bold text-[14px] leading-[16.8px] text-[#f49e26]">
                        {plan.errorMessage}
                      </p>
                    )}
                    <p className="font-['Inter'] font-medium text-[14px] leading-[16.8px] text-[#828382]">
                      You can cancel it anytime
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageMembership;

